import React, { useState } from 'react';
import { Alert, Box, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';

// ADR-188 D6 — the planned start confirms the PRIMARY contract's joining (the
// server checks it on the primary's leg only). Not the person's hire date: an
// ADR-138 rehire may have kept an earlier one.
const plannedStartOf = (employee) => {
  const primary = (employee?.contracts || []).find((contract) => contract.isPrimary && !contract.endDate);
  const start = primary?.startDate || employee?.contractStartDate || employee?.hireDate || null;
  return start ? String(start).slice(0, 10) : null;
};

const EmployeeMarkNoShow = ({ employee, onValidChange }) => {
  const [formData, setFormData] = useState({
    plannedStart: plannedStartOf(employee),
    reason: ''
  });

  const [touched, setTouched] = useState({
    plannedStart: false,
    reason: false
  });

  const handleDateChange = (newValue) => {
    const newDate = newValue && typeof newValue.isValid === 'function' && newValue.isValid()
      ? newValue.format('YYYY-MM-DD')
      : null;

    const newFormData = {
      ...formData,
      plannedStart: newDate
    };
    setFormData(newFormData);
    setTouched(prev => ({
      ...prev,
      plannedStart: true
    }));

    // Always valid since both fields are optional
    onValidChange(true, newFormData);
  };

  const handleInputChange = (field) => (event) => {
    const newFormData = {
      ...formData,
      [field]: event.target.value
    };
    setFormData(newFormData);
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Always valid since both fields are optional
    onValidChange(true, newFormData);
  };

  React.useEffect(() => {
    // Initialize as valid with initial data
    onValidChange(true, formData);
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
        <Alert severity="warning">
          This records that the person <strong>never started</strong>: every contract due to
          start is voided and the person becomes <em>cancelled</em> — the same outcome as a
          withdrawal, kept as a distinct fact in the history. Available only within the
          no-show window after the planned start.
          {employee?.hasPendingResignation && (
            <> The scheduled <strong>contract end is withdrawn in the same step</strong>, so
            nothing is left for the scheduler to enact.</>
          )}
        </Alert>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <DatePicker
            label="Planned Start Date"
            value={formData.plannedStart ? dayjs(formData.plannedStart) : null}
            onChange={handleDateChange}
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: 'Optional - The date the employee was expected to start',
                inputProps: {
                  placeholder: 'DD/MM/YYYY'
                }
              }
            }}
          />
        </LocalizationProvider>

        <TextField
          fullWidth
          label="Reason"
          value={formData.reason}
          onChange={handleInputChange('reason')}
          multiline
          rows={4}
          helperText="Optional - Additional details about the no-show"
          placeholder="Enter reason for no-show..."
        />
      </Box>
    </Box>
  );
};

export default EmployeeMarkNoShow;
